import { readableColor } from 'polished';
import styled from 'styled-components';
import { fontTitle } from 'Styles/fonts';
import * as colors from 'Styles/colors';
import readableText from 'Styles/readable-text';

import { BORDER_RADIUS } from '../Card/Card.styled';
import { Button as StyledButton } from '../Button/Button.styled';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import { getStyle } from '../../styles/cardFontStyles';

const LARGE_CTA_PADDING = '13px 16px';
const MEDIUM_CTA_PADDING = '10px 16px';
const SMALL_CTA_PADDING = '6px 16px';

export const ContentCard = styled.div`
  background-color: ${({ $backgroundColor }) =>
    // if colors[$backgroundColor] is undefined, we render what we've been given because its a custom background color
    colors[$backgroundColor] || $backgroundColor};
  border-radius: ${BORDER_RADIUS};
  grid-column: ${({ $gridSpan }) => `span ${$gridSpan}`};
  position: relative;
  height: fit-content;
`;

export const Cta = styled(StyledButton)`
  margin: 4px 8px 8px 0;
  ${({ $backgroundColor }) => readableText(readableColor($backgroundColor))};
  overflow-wrap: anywhere;

  /* When text wraps, specifying a top/bottom padding will keeps wrapping text away from the edges. */
  ${({ size = 'small' }) => {
    if (size === 'large') return `padding: ${LARGE_CTA_PADDING};`;
    if (size === 'medium') return `padding: ${MEDIUM_CTA_PADDING};`;
    if (size === 'small') return `padding: ${SMALL_CTA_PADDING};`;
  }}

  &:last-of-type {
    margin-right: 0px;
  }
`;

export const Subheader = styled.div`
  a {
    color: unset;
    text-decoration: unset;
  }

  h2 {
    ${({ $responsiveStyleSize = 'regular' }) =>
      getStyle($responsiveStyleSize, 'title')}
    margin-bottom: 4px;
  }

  p {
    ${({ $responsiveStyleSize = 'regular' }) =>
      getStyle($responsiveStyleSize, 'text')}
  }
`;

export const Title = styled.h3`
  ${({ $responsiveStyleSize = 'regular', $titleFontSize }) =>
    $titleFontSize
      ? fontTitle($titleFontSize)
      : getStyle($responsiveStyleSize, 'title')}
  color: ${({ $backgroundColor }) => readableColor($backgroundColor)};
  margin-bottom: 4px;
`;

export const Copy = styled.div`
  color: ${({ $backgroundColor }) => readableColor($backgroundColor)};
  text-align: ${({ $alignment }) => $alignment};

  ${({ $responsiveStyleSize, $withPadding }) => {
    const paddingValue = getStyle($responsiveStyleSize || 'regular', 'padding');
    const font = getStyle($responsiveStyleSize || 'regular', 'text');
    return `${font}; padding: ${$withPadding ? paddingValue : '8px 0 0'};`;
  }}

  & ${Cta} {
    margin-top: 8px;
  }

  & ${Title} + ${Cta} {
    margin-top: 4px;
  }

  *:last-child {
    margin-bottom: 0;
  }
`;

export const Ctas = styled.div`
  align-items: center;
  border-radius: ${BORDER_RADIUS};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: ${({ $responsiveStyleSize }) =>
    $responsiveStyleSize === 'mega' ? '24px' : '12px'};

  ${Cta} {
    margin: 8px 0 0 0;
    max-width: 250px;
    width: 100%;
    text-align: center;
  }

  ${Cta}:first-of-type {
    margin-top: 0;
  }
`;

export const CtasBackdrop = styled.div`
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  background-color: ${colors.grey20};
  opacity: 0.5;
`;

export const CtasContainer = styled.div`
  border-radius: ${BORDER_RADIUS};
  height: calc(100% + 2px);
  position: absolute;
  left: 0;
  top: -2px;
  width: 100%;
`;

export const Image = styled(ResponsiveImage)`
  position: relative;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
  img {
    border-radius: ${({ $isTransparent }) =>
      $isTransparent ? BORDER_RADIUS : `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`};
  }
`;

export const Link = styled.a`
  text-decoration: unset;
  color: unset;
`;
